import React, { useEffect, useMemo, useRef, useState } from "react";
import "./CenterSnapStrip.css";

const PILL_INSET_V = 4;
const PILL_INSET_H = 6;

// Sizing is driven by the measured container, not the viewport: this strip
// lives inside a dialog column whose width is unrelated to the window's.
const MIN_TAB_WIDTH = 56; // below this, labels start colliding
const MAX_TAB_WIDTH = 120; // above this, a 3-char label looks marooned
const MIN_STRIP_HEIGHT = 32;
const MAX_STRIP_HEIGHT = 40;

const CenterSnapStrip = ({
  labels,
  selectedIndex,
  onSelect,
  backgroundColor,
  textColor,
  accessibilityLabels,
  selectedTabColor,
  selectedTextColor,
  maxVisibleTabs,
}) => {
  const scrollRef = useRef(null);

  const [stripWidth, setStripWidth] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const isRecenteringRef = useRef(false);

  const COUNT = labels.length;

  /*
   * Triple the labels so we can scroll
   * continuously in both directions.
   */
  const loopedLabels = useMemo(() => {
    return [...labels, ...labels, ...labels];
  }, [labels]);

  /*
   * How many tabs fit is derived from the measured width rather than fixed by
   * the caller, so the same strip works in a narrow dialog column and across a
   * full-width card. `maxVisibleTabs` is now an optional ceiling.
   */
  const numVisible = useMemo(() => {
    if (!COUNT) return 1;
    if (!stripWidth) return Math.min(COUNT, maxVisibleTabs || COUNT);

    const fitAtMin = Math.floor(stripWidth / MIN_TAB_WIDTH);
    const fitAtMax = Math.ceil(stripWidth / MAX_TAB_WIDTH);
    const ceiling = maxVisibleTabs ? Math.min(COUNT, maxVisibleTabs) : COUNT;

    return Math.max(1, Math.min(ceiling, Math.max(fitAtMax, Math.min(fitAtMin, COUNT))));
  }, [COUNT, stripWidth, maxVisibleTabs]);

  const tabWidth = stripWidth > 0 && numVisible > 0 ? Math.floor(stripWidth / numVisible) : 0;

  // Height and type scale with the tab so the strip stays proportionate at any
  // container width. The old fixed 36px + viewport media queries disagreed with
  // each other, and the inline height made the media rule dead code.
  const stripHeight = tabWidth
    ? Math.round(Math.min(MAX_STRIP_HEIGHT, Math.max(MIN_STRIP_HEIGHT, tabWidth * 0.5)))
    : MIN_STRIP_HEIGHT;

  const fontSize = tabWidth ? Math.round(Math.min(14, Math.max(11, tabWidth * 0.22))) : 12;

  const pillHeight = stripHeight - PILL_INSET_V * 2;

  const pillWidth = tabWidth > 0 ? Math.max(0, Math.round(tabWidth - PILL_INSET_H * 2)) : 0;

  const sidePadding = stripWidth > 0 ? Math.round((stripWidth - tabWidth) / 2) : 0;

  /*
   * Get the width of the strip.
   */
  useEffect(() => {
    const element = scrollRef.current;

    if (!element) return;

    let resizeTimeout;
    let lastWidth = -1;

    const updateWidth = () => {
      const currentWidth = element.clientWidth;

      if (currentWidth !== lastWidth) {
        lastWidth = currentWidth;
        setStripWidth(currentWidth);

        // Reset timeout when size changes
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          // Final validation: ensure we're at stable width
          const finalWidth = element.clientWidth;
          if (finalWidth !== currentWidth) {
            setStripWidth(finalWidth);
          }
        }, 300);
      }
    };

    updateWidth();

    // ResizeObserver for element size changes
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(element);

    // Window resize listener with debounce for popup animations
    const handleWindowResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateWidth, 300);
    };

    window.addEventListener('resize', handleWindowResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleWindowResize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  /*
   * Keep selected item centered.
   *
   * We always target the middle copy:
   *
   * [copy 1] [copy 2] [copy 3]
   *           ^
   *       selected
   */
  useEffect(() => {
    if (!stripWidth || !tabWidth || !COUNT) {
      return;
    }

    const targetIndex = COUNT + selectedIndex;

    const targetPosition = targetIndex * tabWidth;

    isRecenteringRef.current = true;

    const timeout = setTimeout(() => {
      scrollRef.current?.scrollTo({
        left: targetPosition,
        behavior: "smooth",
      });
    }, 0);

    const resetTimeout = setTimeout(() => {
      isRecenteringRef.current = false;
    }, 450);

    return () => {
      clearTimeout(timeout);
      clearTimeout(resetTimeout);
    };
  }, [selectedIndex, stripWidth, tabWidth, COUNT]);

  /*
   * Update scroll position.
   */
  const handleScroll = (event) => {
    setScrollLeft(event.currentTarget.scrollLeft);
  };

  /*
   * Determine selected tab after scrolling.
   */
  useEffect(() => {
    if (!tabWidth || !COUNT || isRecenteringRef.current) {
      return;
    }

    const timer = setTimeout(() => {
      const rawIndex = Math.round(scrollLeft / tabWidth);

      const realIndex = ((rawIndex % COUNT) + COUNT) % COUNT;

      /*
       * Update parent selection.
       */
      if (realIndex !== selectedIndex) {
        onSelect(realIndex);
        return;
      }

      /*
       * If we're in copy 1 or copy 3,
       * silently move to copy 2.
       */
      const middleIndex = COUNT + realIndex;

      if (rawIndex !== middleIndex) {
        isRecenteringRef.current = true;

        scrollRef.current?.scrollTo({
          left: middleIndex * tabWidth,
          behavior: "auto",
        });

        setTimeout(() => {
          isRecenteringRef.current = false;
        }, 50);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [scrollLeft, tabWidth, COUNT, selectedIndex, onSelect]);

  /*
   * Handle clicking a tab.
   */
  const handleTabPress = (index) => {
    const realIndex = index % COUNT;

    if (realIndex !== selectedIndex) {
      onSelect(realIndex);
    }
  };

  return (
    <div
      className="center-snap-strip"
      style={{
        height: stripHeight,
        borderRadius: Math.round(stripHeight / 2),
        backgroundColor,
      }}
    >
      {/* Center pill */}
      {stripWidth > 0 && (
        <div
          className="center-snap-strip__pill"
          style={{
            left: Math.round((stripWidth - pillWidth) / 2),

            width: pillWidth,

            height: pillHeight,

            borderRadius: Math.round(pillHeight / 2),

            backgroundColor: selectedTabColor || "#ffffff",
          }}
        />
      )}

      {/* Scrollable tabs */}
      <div
        ref={scrollRef}
        className="center-snap-strip__scroll"
        role="tablist"
        style={{
          paddingLeft: sidePadding,
          paddingRight: sidePadding,
        }}
        onScroll={handleScroll}
      >
        {loopedLabels.map((label, index) => {
          const realIndex = index % COUNT;

          const isSelected = realIndex === selectedIndex;

          return (
            <button
              key={`${index}-${label}`}
              type="button"
              className="center-snap-strip__tab"
              role="tab"
              style={{
                width: tabWidth,
              }}
              onClick={() => handleTabPress(index)}
              aria-label={accessibilityLabels?.[realIndex] || label}
              aria-selected={isSelected}
            >
              <span
                className="center-snap-strip__text"
                style={{
                  fontSize,
                  color: selectedIndex === realIndex ? selectedTextColor : textColor,
                }}
              >
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default CenterSnapStrip;
